// src/modules/leads/LeadAddressMap.js
//
// Map for the Addresses section of a lead. Pins the primary address plus every
// additional LeadAddress that has coordinates, and auto-fits to show them all.
//
// Coordinates are geocoded on save, but not every row has them — 134 of ~887
// leads have no primary lat/lng — so this degrades to an explanatory empty state
// rather than rendering a map centred on the ocean.
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { FaMapMarkedAlt, FaExclamationCircle } from 'react-icons/fa';
import useGoogleMapsLoader from './route-planner/useGoogleMapsLoader';
import { darkMapStyles } from './route-planner/routeUtils';

const PRIMARY_COLOR = '#F69620';   // brand orange — the lead's main address
const SECONDARY_COLOR = '#3d5afe'; // additional addresses

const MapBox = styled.div`
  width: 100%;
  height: 260px;
  border-radius: ${p => p.theme.borderRadius};
  overflow: hidden;
  margin-top: ${p => p.theme.spacing(1.5)};
  border: 1px solid ${p => p.theme.colors.inputGray}33;
`;

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 120px;
  margin-top: ${p => p.theme.spacing(1.5)};
  border-radius: ${p => p.theme.borderRadius};
  border: 1px dashed ${p => p.theme.colors.inputGray}55;
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.85rem;
  text-align: center;
  padding: 0 16px;

  svg { font-size: 1.4rem; opacity: 0.6; }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 8px;
  font-size: 0.75rem;
  color: ${p => p.theme.colors.textSecondary};

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  i {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    display: inline-block;
  }
`;

const createPin = (color) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
      <circle cx="14" cy="14" r="6" fill="#fff"/>
    </svg>`;
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
};

const escapeHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const formatLine = (a) => [a.street, a.city, a.state, a.zipcode].filter(Boolean).join(', ');

const hasCoords = (a) =>
  a && a.latitude !== null && a.latitude !== undefined && a.latitude !== '' &&
  a.longitude !== null && a.longitude !== undefined && a.longitude !== '' &&
  Number.isFinite(Number(a.latitude)) && Number.isFinite(Number(a.longitude));

/**
 * @param {object} lead          the lead (its own street/lat/lng = primary pin)
 * @param {array}  addresses     LeadAddress rows
 * @param {object} [focusTarget] { id, n } — pan to this address (id null = the
 *                               primary pin). The nonce `n` lets the same card
 *                               be clicked repeatedly to re-focus.
 */
const LeadAddressMap = ({ lead, addresses = [], focusTarget = null }) => {
  const { isLoaded, error } = useGoogleMapsLoader();
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoRef = useRef(null);

  // Primary first, then the additional addresses — only those we can actually place.
  const points = useMemo(() => {
    const list = [];
    if (hasCoords(lead)) {
      list.push({
        key: 'primary',
        id: null,
        label: 'Primary',
        text: formatLine(lead),
        lat: Number(lead.latitude),
        lng: Number(lead.longitude),
        isPrimary: true,
      });
    }
    (addresses || []).forEach((a) => {
      if (!hasCoords(a)) return;
      list.push({
        key: `addr-${a.id}`,
        id: a.id,
        label: a.label || 'Address',
        text: formatLine(a),
        lat: Number(a.latitude),
        lng: Number(a.longitude),
        isPrimary: false,
      });
    });
    return list;
  }, [lead, addresses]);

  // How many rows exist but can't be shown — worth saying out loud rather than
  // silently omitting a pin the user expects to see.
  const missingCount = useMemo(() => {
    const total = (hasCoords(lead) || lead?.street ? 1 : 0) + (addresses?.length || 0);
    return Math.max(0, total - points.length);
  }, [lead, addresses, points.length]);

  const openInfo = useCallback((marker, point) => {
    if (!infoRef.current) return;
    infoRef.current.setContent(`
      <div style="padding:6px 8px;font-family:sans-serif;color:#222;min-width:170px;">
        <div style="font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:${point.isPrimary ? PRIMARY_COLOR : SECONDARY_COLOR};font-weight:700;margin-bottom:3px;">${escapeHtml(point.label)}</div>
        <div style="font-size:13px;line-height:1.45;">${escapeHtml(point.text) || 'No street address'}</div>
        <a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(point.text)}"
           target="_blank" rel="noopener noreferrer"
           style="display:inline-block;margin-top:6px;font-size:12px;color:#3d5afe;text-decoration:none;">Directions →</a>
      </div>`);
    infoRef.current.open(mapRef.current, marker);
  }, []);

  // Build / rebuild the map whenever the set of points changes.
  useEffect(() => {
    if (!isLoaded || !mapNodeRef.current || points.length === 0) return;

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(mapNodeRef.current, {
        zoom: 13,
        center: { lat: points[0].lat, lng: points[0].lng },
        styles: darkMapStyles,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
      });
      infoRef.current = new window.google.maps.InfoWindow();
    }

    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => {
      const marker = new window.google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        map: mapRef.current,
        title: `${point.label} — ${point.text}`,
        icon: {
          url: createPin(point.isPrimary ? PRIMARY_COLOR : SECONDARY_COLOR),
          scaledSize: new window.google.maps.Size(26, 37),
          anchor: new window.google.maps.Point(13, 37),
        },
        zIndex: point.isPrimary ? 2 : 1,
      });
      marker.__point = point;
      marker.addListener('click', () => openInfo(marker, point));
      markersRef.current.push(marker);
      bounds.extend({ lat: point.lat, lng: point.lng });
    });

    if (points.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(15);
    } else {
      mapRef.current.fitBounds(bounds, 48);
    }
  }, [isLoaded, points, openInfo]);

  // Clicking an address card focuses its pin.
  useEffect(() => {
    if (!focusTarget || !mapRef.current) return;
    const wanted = focusTarget.id ?? null;
    const marker = markersRef.current.find(m => m.__point.id === wanted);
    if (!marker) return; // that address has no coordinates to pan to
    mapRef.current.panTo(marker.getPosition());
    mapRef.current.setZoom(16);
    openInfo(marker, marker.__point);
  }, [focusTarget, openInfo]);

  // Tear down listeners/markers on unmount so a revisit starts clean.
  useEffect(() => () => {
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    mapRef.current = null;
    infoRef.current = null;
  }, []);

  if (error) {
    return (
      <Placeholder>
        <FaExclamationCircle />
        <span>Map couldn&apos;t load. Check the Google Maps API key.</span>
      </Placeholder>
    );
  }

  if (points.length === 0) {
    return (
      <Placeholder>
        <FaMapMarkedAlt />
        <span>
          No mapped location yet — this lead&apos;s address hasn&apos;t been geocoded.
          Editing and saving the address will place it on the map.
        </span>
      </Placeholder>
    );
  }

  return (
    <>
      {!isLoaded ? (
        <Placeholder><FaMapMarkedAlt /><span>Loading map…</span></Placeholder>
      ) : (
        <MapBox ref={mapNodeRef} />
      )}
      <Legend>
        <span><i style={{ background: PRIMARY_COLOR }} /> Primary</span>
        {points.some(p => !p.isPrimary) && (
          <span><i style={{ background: SECONDARY_COLOR }} /> Additional ({points.filter(p => !p.isPrimary).length})</span>
        )}
        {missingCount > 0 && (
          <span title="These addresses have no coordinates yet">
            {missingCount} not mapped
          </span>
        )}
      </Legend>
    </>
  );
};

export default LeadAddressMap;
